export interface AgentConfigFetchOptions {
  readonly signal: AbortSignal;
  readonly fetchImpl?: typeof fetch;
  readonly retryDelaysMs?: readonly number[];
  readonly sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
}

const DEFAULT_RETRY_DELAYS_MS = [250, 750] as const;

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('The operation was aborted.', 'AbortError');
}

async function abortableSleep(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) throw abortReason(signal);
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      reject(abortReason(signal));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Fetch public agent configuration with a small network-only retry budget.
 * HTTP responses retain their existing semantics (404, 410, and paused 503)
 * and are never retried here; only fetch rejections such as an Android
 * WebView's transient "Failed to fetch" receive bounded backoff.
 */
export async function fetchAgentConfigResponse(
  url: string,
  options: AgentConfigFetchOptions,
): Promise<Response> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const retryDelays = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
  const sleep = options.sleep ?? abortableSleep;

  for (let attempt = 0; ; attempt += 1) {
    if (options.signal.aborted) throw abortReason(options.signal);
    try {
      return await fetchImpl(url, {
        headers: { accept: 'application/json' },
        signal: options.signal,
      });
    } catch (error) {
      if (options.signal.aborted) throw abortReason(options.signal);
      const delay = retryDelays[attempt];
      if (delay === undefined) throw error;
      await sleep(delay, options.signal);
    }
  }
}
